import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { ToastProvider } from './contexts/ToastContext';
import { VoiceProvider } from './contexts/VoiceContext';

// Error Boundary
import ErrorBoundary from './components/ErrorBoundary';

// Protected Route Component
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Legacy Components (for backward compatibility)
import ChatPage from './pages/ChatPage';

// Role Selection and Login Pages
import RoleSelectionPage from './pages/RoleSelectionPage';
import StudentLoginPage from './pages/StudentLoginPage';
import TeacherLoginPage from './pages/TeacherLoginPage';
import SchoolAdminLoginPage from './pages/SchoolAdminLoginPage';
import SuperAdminLoginPage from './pages/SuperAdminLoginPage';

// Registration Pages
import StudentRegisterPage from './pages/StudentRegisterPage';
import TeacherRegisterPage from './pages/TeacherRegisterPage';
import SchoolAdminRegisterPage from './pages/SchoolAdminRegisterPage';

// Dashboard Pages
import StudentDashboard from './pages/Dashboard/StudentDashboard';
import TeacherDashboard from './pages/Dashboard/TeacherDashboard';
import SchoolAdminDashboard from './pages/Dashboard/SchoolAdminDashboard';
import SuperAdminDashboard from './pages/Dashboard/SuperAdminDashboard';
import SchoolDetailPage from './pages/Dashboard/SchoolDetailPage';
import StudentDetailPage from './pages/Dashboard/StudentDetailPage';
import ClassManagementPage from './pages/Teacher/ClassManagementPage';
import AnalyticsPage from './pages/Teacher/AnalyticsPage';
import InsightsPage from './pages/Teacher/InsightsPage';
import SettingsPage from './pages/Teacher/SettingsPage';

// School Admin Pages
import SchoolAdminTeachersPage from './pages/SchoolAdmin/TeachersPage';
import SchoolAdminStudentsPage from './pages/SchoolAdmin/StudentsPage';
import SchoolAdminAnalyticsPage from './pages/SchoolAdmin/AnalyticsPage';
import SchoolAdminSettingsPage from './pages/SchoolAdmin/SettingsPage';

// Student Pages
import StudentSettingsPage from './pages/Student/SettingsPage';

// Super Admin Pages
import SuperAdminAnalyticsPage from './pages/SuperAdmin/AnalyticsPage';
import SuperAdminCodesPage from './pages/SuperAdmin/CodesPage';
import SuperAdminSettingsPage from './pages/SuperAdmin/SettingsPage';

// Main App with Providers and Routes
function App() {
  return (
    <ErrorBoundary>
      <div className="App min-h-screen w-full bg-transparent">
        <AuthProvider>
          <ChatProvider>
            <VoiceProvider>
            <ToastProvider>
            <Router>
              <Routes>
              {/* Public Routes - Role Selection and Login */}
              <Route path="/" element={<RoleSelectionPage />} />
              <Route path="/role-selection" element={<RoleSelectionPage />} />

              {/* Login Routes */}
              <Route path="/login/student" element={<StudentLoginPage />} />
              <Route path="/login/teacher" element={<TeacherLoginPage />} />
              <Route path="/login/school-admin" element={<SchoolAdminLoginPage />} />
              <Route path="/login/super-admin" element={<SuperAdminLoginPage />} />

              {/* Legacy login route - redirect to role selection */}
              <Route path="/login" element={<Navigate to="/role-selection" replace />} />

              {/* Registration Routes */}
              <Route path="/register/student" element={<StudentRegisterPage />} />
              <Route path="/register/teacher" element={<TeacherRegisterPage />} />
              <Route path="/register/school-admin" element={<SchoolAdminRegisterPage />} />

              {/* Student Routes */}
              <Route path="/student/dashboard" element={
                <ProtectedRoute allowedRoles="student">
                  <StudentDashboard />
                </ProtectedRoute>
              } />
              <Route path="/student/chat" element={
                <ProtectedRoute allowedRoles="student">
                  <ChatPage />
                </ProtectedRoute>
              } />
              <Route path="/student/settings" element={
                <ProtectedRoute allowedRoles="student">
                  <StudentSettingsPage />
                </ProtectedRoute>
              } />

              {/* Teacher Routes */}
              <Route path="/teacher/dashboard" element={
                <ProtectedRoute allowedRoles="teacher">
                  <TeacherDashboard />
                </ProtectedRoute>
              } />
              <Route path="/teacher/students" element={
                <ProtectedRoute allowedRoles="teacher">
                  <TeacherDashboard />
                </ProtectedRoute>
              } />
              <Route path="/teacher/student/:studentId" element={
                <ProtectedRoute allowedRoles="teacher">
                  <StudentDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/teacher/insights" element={
                <ProtectedRoute allowedRoles="teacher">
                  <InsightsPage />
                </ProtectedRoute>
              } />
              <Route path="/teacher/analytics" element={
                <ProtectedRoute allowedRoles="teacher">
                  <AnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/teacher/classes" element={
                <ProtectedRoute allowedRoles="teacher">
                  <ClassManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/teacher/settings" element={
                <ProtectedRoute allowedRoles="teacher">
                  <SettingsPage />
                </ProtectedRoute>
              } />

              {/* School Admin Routes */}
              <Route path="/school-admin/dashboard" element={
                <ProtectedRoute allowedRoles="school-admin">
                  <SchoolAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/school-admin/teachers" element={
                <ProtectedRoute allowedRoles="school-admin">
                  <SchoolAdminTeachersPage />
                </ProtectedRoute>
              } />
              <Route path="/school-admin/students" element={
                <ProtectedRoute allowedRoles="school-admin">
                  <SchoolAdminStudentsPage />
                </ProtectedRoute>
              } />
              <Route path="/school-admin/analytics" element={
                <ProtectedRoute allowedRoles="school-admin">
                  <SchoolAdminAnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/school-admin/settings" element={
                <ProtectedRoute allowedRoles="school-admin">
                  <SchoolAdminSettingsPage />
                </ProtectedRoute>
              } />

              {/* Super Admin Routes */}
              <Route path="/super-admin/dashboard" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/super-admin/schools" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/super-admin/school/:schoolId" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SchoolDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/super-admin/analytics" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SuperAdminAnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/super-admin/codes" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SuperAdminCodesPage />
                </ProtectedRoute>
              } />
              <Route path="/super-admin/settings" element={
                <ProtectedRoute allowedRoles="super-admin">
                  <SuperAdminSettingsPage />
                </ProtectedRoute>
              } />

              {/* Legacy chat route - redirect based on user role */}
              <Route path="/chat" element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              } />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/role-selection" replace />} />
            </Routes>
          </Router>
            </ToastProvider>
            </VoiceProvider>
        </ChatProvider>
      </AuthProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;
